import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import CodeBlock from '@dashboard/components/CodeBlock';
import RadioCard from '@dashboard/components/Form/components/RadioCard';
import { overlayUrl } from '@dashboard/services/env';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import getPageTypeLabel from '@dashboard/services/getPageTypeLabel';
import { getEmbedCode, getPopupCode } from '@repo/lib';

const EMBED_METHODS = {
  RESPONSIVE: 'responsive',
  POPUP: 'popup',
};

function getIndefiniteArticle(value) {
  return /^[aeiou]/i.test(value) ? 'an' : 'a';
}

function capitalizeFirst(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const AddToWebsiteOverlay = ({ page, seller, onBack }) => {
  const [selectedMethod, setSelectedMethod] = useState(EMBED_METHODS.RESPONSIVE);
  const checkoutUrl = getCheckoutUrl(page, seller);
  const embedCode = getEmbedCode({ checkoutUrl, overlayUrl });
  const { scriptCode, popUpCode } = getPopupCode({ checkoutUrl, overlayUrl });
  const directEmbedLabel = getPageTypeLabel(page);
  const directEmbedArticle = getIndefiniteArticle(directEmbedLabel);

  return (
    <div className="text-left h-full flex-1 flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-muted transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gray-950 dark:focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Go back"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-700 dark:text-muted-foreground" />
        </button>
        <h2 className="text-lg font-bold">Add to website</h2>
      </div>

      <div className="mb-6">
        <RadioCard.Group name="embedMethod" isInline={false}>
          <RadioCard.Card
            name="embedMethod"
            value={EMBED_METHODS.RESPONSIVE}
            checked={selectedMethod === EMBED_METHODS.RESPONSIVE}
            onChange={() => setSelectedMethod(EMBED_METHODS.RESPONSIVE)}
          >
            <RadioCard.Label>Responsive embed</RadioCard.Label>
            <RadioCard.LabelDescription>
              Show the {directEmbedLabel} directly on your page
            </RadioCard.LabelDescription>
          </RadioCard.Card>
          <RadioCard.Card
            name="embedMethod"
            value={EMBED_METHODS.POPUP}
            checked={selectedMethod === EMBED_METHODS.POPUP}
            onChange={() => setSelectedMethod(EMBED_METHODS.POPUP)}
          >
            <RadioCard.Label>{capitalizeFirst(directEmbedLabel)} pop-up</RadioCard.Label>
            <RadioCard.LabelDescription>
              Open {directEmbedArticle} {directEmbedLabel} pop-up over your page content
            </RadioCard.LabelDescription>
          </RadioCard.Card>
        </RadioCard.Group>
      </div>

      {selectedMethod === EMBED_METHODS.RESPONSIVE && (
        <div>
          <p className="text-sm text-gray-600 dark:text-muted-foreground mb-2">
            1. Add the embed code to your website
          </p>
          <CodeBlock copy>{embedCode}</CodeBlock>
        </div>
      )}

      {selectedMethod === EMBED_METHODS.POPUP && (
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-muted-foreground mb-2">
              1. Add this script to the <b>header</b> of your website
            </p>
            <CodeBlock copy>{scriptCode}</CodeBlock>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-muted-foreground mb-2">
              2. Add this URL to a <b>link</b> or <b>button</b>. Make sure it has the class{' '}
              <b>cp-button</b>
            </p>
            <CodeBlock copy>{popUpCode}</CodeBlock>
          </div>
        </div>
      )}

      <div className="mt-6">
        <div className="font-medium text-sm underline">
          <a
            href={
              selectedMethod === EMBED_METHODS.RESPONSIVE
                ? '/docs/articles/embed-your-checkout-on-your-website'
                : '/docs/articles/add-a-pop-up-checkout-to-your-site'
            }
            target="_blank"
            className="flex items-center"
            rel="noreferrer"
          >
            Read step-by-step instructions
            <ArrowTopRightOnSquareIcon className="ml-1 w-5 h-5 text-gray-600 dark:text-muted-foreground" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default AddToWebsiteOverlay;
